import { useState } from "react";

import { api, type PastTournament, type HallOfFameEntry } from "../api";
import { MemberAvatar } from "../avatar";
import { useLoad } from "../useLoad";

interface Props {
  teamId: string;
  revision: number;
  onError: (message: string) => void;
}

type Board = "members" | "vehicles" | "history";

/**
 * All-time records. Tasks 12.2 through 12.6.
 *
 * Three boards over the same afternoon: who predicts best, which truck wins most, and what actually
 * happened. All of it is arithmetic over immutable facts done at read time — requirement 10.8 rules out
 * maintained totals because they drift, and drift here is the worst kind of bug: somebody's number is
 * quietly wrong and nobody can tell when it went wrong.
 */
export function Records({ teamId, revision, onError }: Props) {
  const [board, setBoard] = useState<Board>("members");

  return (
    <>
      <div className="segmented">
        {(
          [
            ["members", "Family"],
            ["vehicles", "Trucks"],
            ["history", "Past jams"],
          ] as [Board, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            className={board === id ? "on" : undefined}
            aria-current={board === id}
            onClick={() => setBoard(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {board === "members" && <MemberBoard teamId={teamId} revision={revision} onError={onError} />}
      {board === "vehicles" && <HallOfFame teamId={teamId} revision={revision} onError={onError} />}
      {board === "history" && <History teamId={teamId} revision={revision} onError={onError} />}
    </>
  );
}

/**
 * Requirements 10.3, 10.4 and 10.5.
 *
 * Victories lead, accuracy breaks ties, and cumulative points is shown but never ranks — it measures
 * attendance, so ranking by it would make a new member permanently last however well they predict.
 */
function MemberBoard({ teamId, revision, onError }: Props) {
  const { data, loading } = useLoad(() => api.memberRecords(teamId), [teamId, revision], onError);
  const lines = data?.lines ?? [];

  if (lines.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          {loading ? "Loading…" : "No finished tournaments yet."}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <p className="section-title">
          {data?.tournaments ?? 0} {data?.tournaments === 1 ? "jam" : "jams"} · ranked by wins
        </p>
        <ul className="list">
          {lines.map((line, index) => (
            <li key={line.sub} className="row">
              <span className={`place${index === 0 && line.victories > 0 ? " won" : ""}`}>
                {index + 1}
              </span>
              <MemberAvatar sub={line.sub} picture={line.picture} name={line.name} />
              <span className="grow">
                <span className="name">{line.name ?? "A member"}</span>
                <span className="note">
                  {line.unplayed ? (
                    // Requirement 10.5 — in the team, not yet played. Worth distinguishing from
                    // somebody who played and scored nothing; the two deserve different sympathy.
                    "Hasn't predicted yet"
                  ) : (
                    <>
                      {line.tournamentsPlayed} played · {Math.round(line.accuracy * 100)}% accurate
                      {line.provisional && <span className="muted"> (early days)</span>}
                      {line.championPicks > 0 && ` · ${line.championPicks} champion picks`}
                    </>
                  )}
                </span>
              </span>
              <span className="points">{line.victories}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="card">
        <p className="section-title">The detail</p>
        <ul className="list">
          {lines
            .filter((line) => !line.unplayed)
            .map((line) => (
              <li key={line.sub} className="vehicle">
                <div className="row">
                  <MemberAvatar sub={line.sub} picture={line.picture} name={line.name} size={24} />
                  <span className="grow name">{line.name ?? "A member"}</span>
                </div>
                {/* Requirement 10.4's supporting stats, out of the ranked list so it stays readable. */}
                <div className="stats">
                  <Stat label="Best jam" value={String(line.bestScore)} />
                  <Stat label="Average" value={line.averagePoints.toFixed(1)} />
                  <Stat label="Right" value={`${line.correctPicks}/${line.scoredPicks}`} />
                  <Stat label="Points" value={String(line.points)} />
                </div>
              </li>
            ))}
        </ul>
        <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
          Accuracy settles down after about {data?.minimumScoredSelections ?? 25} picks. Wins are what
          rank — total points would just reward turning up.
        </p>
      </div>
    </>
  );
}

/** Requirement 10.6 — vehicles all-time. Retired trucks included: they still won what they won. */
function HallOfFame({ teamId, revision, onError }: Props) {
  const { data, loading } = useLoad(() => api.hallOfFame(teamId), [teamId, revision], onError);
  const [open, setOpen] = useState<string | null>(null);
  const vehicles = data?.vehicles ?? [];
  const raced = vehicles.filter((vehicle) => vehicle.heatsRaced > 0);

  if (raced.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          {loading ? "Loading…" : "No races yet. The Hall of Fame fills itself in."}
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <p className="section-title">{raced.length} trucks with a record</p>
      <ul className="list">
        {raced.map((vehicle, index) => (
          <li key={vehicle.vehicleId} className="vehicle">
            <div className="row">
              <span className={`place${index === 0 && vehicle.tournamentsWon > 0 ? " won" : ""}`}>
                {index + 1}
              </span>
              <span className="grow">
                <span className={`name${vehicle.isRetired ? " retired-name" : ""}`}>
                  {vehicle.name ?? "—"}
                </span>
                <span className="note">
                  {vehicle.tournamentsWon > 0
                    ? `${vehicle.tournamentsWon} ${vehicle.tournamentsWon === 1 ? "win" : "wins"} · `
                    : ""}
                  {Math.round(vehicle.winRate * 100)}% of races
                  {vehicle.provisional && <span className="muted"> (early days)</span>}
                  {vehicle.isRetired && " · retired"}
                </span>
              </span>
              <button className="btn small" onClick={() => setOpen(open === vehicle.vehicleId ? null : vehicle.vehicleId)}>
                {open === vehicle.vehicleId ? "Hide" : "Stats"}
              </button>
            </div>
            {open === vehicle.vehicleId && <VehicleStats vehicle={vehicle} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function VehicleStats({ vehicle }: { vehicle: HallOfFameEntry }) {
  return (
    <div className="stats">
      <Stat label="Races" value={String(vehicle.heatsRaced)} />
      <Stat label="Won" value={String(vehicle.heatsWon)} />
      <Stat label="Avg finish" value={vehicle.averageFinish.toFixed(1)} />
      <Stat label="Best streak" value={String(vehicle.longestStreak)} />
      <Stat label="Jams" value={String(vehicle.tournamentsEntered)} />
    </div>
  );
}

/**
 * Requirement 10.7 — a completed tournament is fully replayable, not just a name and a winner.
 *
 * The list comes from the immutable snapshots, which already carry the champion, the theme and the final
 * prediction standings; opening one is a separate read of the tournament itself, where every heat and its
 * finishing order still lives.
 */
function History({ teamId, revision, onError }: Props) {
  const { data, loading } = useLoad(() => api.history(teamId), [teamId, revision], onError);
  const [open, setOpen] = useState<string | null>(null);
  const past = data?.tournaments ?? [];

  if (past.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          {loading ? "Loading…" : "Nothing finished yet. Run a tournament to the end and it lands here."}
        </p>
      </div>
    );
  }

  return (
    <>
      {past.map((tournament) => (
        <div key={tournament.tournamentId} className="card">
          <div className="row">
            <span className="grow">
              <span className="name">{tournament.name ?? "Grayson Earl Jam"}</span>
              <span className="note">
                {tournament.championName ?? "—"} won · {tournament.size} racers,{" "}
                {tournament.width} at a time
                {tournament.completedAt && ` · ${when(tournament.completedAt)}`}
              </span>
            </span>
            <button
              className="btn small"
              onClick={() => setOpen(open === tournament.tournamentId ? null : tournament.tournamentId)}
            >
              {open === tournament.tournamentId ? "Hide" : "Replay"}
            </button>
          </div>

          {tournament.winners.length > 0 && (
            <div className="row" style={{ minHeight: 0, marginTop: 6 }}>
              <span className="note grow">
                {tournament.winners.length === 1 ? "Best guesser" : "Tied for best guesser"}
              </span>
              <div className="avatars">
                {tournament.winners.map((winner) => (
                  <MemberAvatar
                    key={winner.sub}
                    sub={winner.sub}
                    picture={winner.picture}
                    name={winner.name}
                    size={24}
                  />
                ))}
              </div>
            </div>
          )}

          {open === tournament.tournamentId && <Replay teamId={teamId} tournament={tournament} onError={onError} />}
        </div>
      ))}
    </>
  );
}

function Replay({
  teamId,
  tournament,
  onError,
}: {
  teamId: string;
  tournament: PastTournament;
  onError: (message: string) => void;
}) {
  const { data, loading } = useLoad(
    () => api.tournament(teamId, tournament.tournamentId),
    [teamId, tournament.tournamentId],
    onError,
  );

  if (!data) {
    return <p className="muted">{loading ? "Loading…" : "Couldn't load that one."}</p>;
  }

  const names = new Map(data.entrants.map((entrant) => [entrant.vehicleId, entrant.name ?? "—"]));

  return (
    <>
      <p className="section-title" style={{ marginTop: 14 }}>
        Every race
      </p>
      {Array.from({ length: data.roundCount }, (_, index) => index + 1).map((round) => (
        <div key={round} className="bracket-round" style={{ marginBottom: 10 }}>
          <p className="muted" style={{ margin: 0 }}>
            {round === data.roundCount ? "Final" : `Round ${round}`}
          </p>
          {data.heats
            .filter((heat) => heat.round === round)
            .map((heat) => (
              <div key={heat.index} className="heat-chip done">
                <span className="winner">{names.get(heat.finishingOrder?.[0] ?? "") ?? "—"}</span>
                <span className="muted grow">
                  beat{" "}
                  {(heat.finishingOrder ?? [])
                    .slice(1)
                    .map((id) => names.get(id) ?? "—")
                    .join(", ")}
                </span>
                {heat.mediaCount > 0 && <span className="btn small film has">🎬 {heat.mediaCount}</span>}
              </div>
            ))}
        </div>
      ))}

      {tournament.standings.length > 0 && (
        <>
          <p className="section-title">How the guessing went</p>
          <ul className="list">
            {tournament.standings.map((line, index) => (
              <li key={line.sub} className="row">
                <span className={`place${index === 0 && line.points > 0 ? " won" : ""}`}>
                  {index + 1}
                </span>
                <MemberAvatar sub={line.sub} picture={line.picture} name={line.name} size={24} />
                <span className="grow name">{line.name ?? "A member"}</span>
                <span className="points">{line.points}</span>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
            Out of {tournament.maximumPoints} that day.
          </p>
        </>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </span>
  );
}

/** Dates on a family's history want to read as "last Tuesday", not as an ISO string. */
function when(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
